import { CroppingMultipleTargetUpdateOptions, FieldViewModel, IPlanInfo } from "@omnia/shared/fields";
import { RowWithListColumns } from "@omnia/shared/fields/cropYear/RowWithListColumns";
import { PlanType } from "@omnia/shared/generated/enums";
import { labels } from "@omnia/shared/generated/labels";
import { Checkbox } from "@omnia/shared/react/checkbox";
import { CropLabel } from "@omnia/shared/react/CropLabel";
import { DialogBody } from "@omnia/shared/react/DialogBody";
import { DialogFooter, DialogFooterButtons } from "@omnia/shared/react/DialogFooter";
import { DialogHeader } from "@omnia/shared/react/DialogHeader";
import { classes } from "@omnia/shared/react/react";
import { AngularReactDialogContext } from "@omnia/shared/react/reactAngularDialog";
import { sortBy, toggle } from "@omnia/shared/utils/array";
import { PropsWithChildren, ReactNode, useCallback, useMemo, useState } from "react";
import "./styles.less";

export type EditMultipleCroppingsConfirmationDialogProps = {
    targets: FieldViewModel[];
    affectedPlans: IPlanInfo[];
    managementConfig: ReturnType<typeof omnia.crops.getCropManagementConfig>;
};

const classNamePrefix = "omnia-edit-multiple-croppings-confirmation-dialog";

type TargetProperty = keyof CroppingMultipleTargetUpdateOptions;

function getPropertyOptions(
    managementConfig: EditMultipleCroppingsConfirmationDialogProps["managementConfig"]
): { key: TargetProperty; label: string }[] {
    return [
        { key: "CropTab", label: "All crop details, excluding crop description" },
        { key: "CropDescription", label: "Crop description only" },
        { key: "InfoTab", label: "All crop info" },
        managementConfig?.enabled ? { key: "ManagementOptionsTab", label: `All ${managementConfig.title}` } : undefined,
    ].filter(Boolean) as { key: TargetProperty; label: string }[];
}

type DialogResult =
    | { confirmed: false }
    | { confirmed: true; croppingIds: number[]; multipleTargetOptions: CroppingMultipleTargetUpdateOptions };

export const EditMultipleCroppingsConfirmationDialog = ({
    arguments: { targets, affectedPlans, managementConfig },
    dependencies,
    close,
}: AngularReactDialogContext<EditMultipleCroppingsConfirmationDialogProps, {}, DialogResult>) => {
    const [selectedProperties, setSelectedProperties] = useState<TargetProperty[]>([]);

    const propertyOptions = useMemo(() => getPropertyOptions(managementConfig), [managementConfig]);

    const isValid = !!selectedProperties.length;

    const cancel = useCallback(() => close({ confirmed: false }), []);
    const confirm = useCallback(
        () =>
            close({
                confirmed: true,
                croppingIds: targets.map((x) => x.croppingId),
                multipleTargetOptions: {
                    CropTab: selectedProperties.includes("CropTab"),
                    CropDescription: selectedProperties.includes("CropDescription"),
                    InfoTab: selectedProperties.includes("InfoTab"),
                    ManagementOptionsTab: selectedProperties.includes("ManagementOptionsTab"),
                },
            }),
        [targets, selectedProperties]
    );

    const showPlanWarning =
        !!affectedPlans.length && selectedProperties.includes("CropTab");

    const sortedTargets = useMemo(
        () => sortBy(targets.map((target) => ({
            label: target.getCroppingDescription(true, true, true, true),
            target,
        })), ["label"]),
        [targets]
    );

    const sortedPlans = useMemo(() => sortBy(affectedPlans, ["Type", "Name"]), [affectedPlans]);

    return (
        <div className={classNamePrefix}>
            <DialogHeader icon="fas fa-pen-field">Allocate changes to multiple croppings</DialogHeader>
            <DialogBody className="dialog-body">
                <div className="sections">
                    <Section title={`Target Croppings (${targets.length} selected)`}>
                        <p>The following croppings will be updated:</p>
                        <RowWithListColumns
                            columns={1}
                            items={sortedTargets}
                            renderItem={(item) => (
                                <Checkbox checked disabled>
                                    <CropLabel color={item.target.cssColor}>{item.label}</CropLabel>
                                </Checkbox>
                            )}
                        />
                    </Section>
                    <Section title="Target Properties">
                        <p>Select properties to update:</p>
                        <RowWithListColumns
                            columns={1}
                            items={propertyOptions}
                            renderItem={(item) => (
                                <Checkbox
                                    checked={selectedProperties.includes(item.key)}
                                    setChecked={(value) =>
                                        setSelectedProperties((prev) => toggle(prev, item.key, value))
                                    }
                                >
                                    {item.label}
                                </Checkbox>
                            )}
                        />
                    </Section>
                    {showPlanWarning ? (
                        <Section
                            icon="fas fa-exclamation-triangle"
                            title={`Affected Plans (${affectedPlans.length})`}
                            className="warning"
                        >
                            <p>The selected croppings are already part of the following plans:</p>
                            <RowWithListColumns
                                columns={1}
                                items={sortedPlans}
                                renderItem={(plan) => <PlanLabel plan={plan} />}
                            />
                            <p>You may need to recalculate requirements or plans afterwards.</p>
                        </Section>
                    ) : null}
                </div>
            </DialogBody>
            <DialogFooter>
                <DialogFooterButtons
                    onClose={cancel}
                    onCommit={confirm}
                    saveButtonText="Update All"
                    canCommit={isValid}
                    hasChanges={true}
                />
            </DialogFooter>
        </div>
    );
};

function Section({
    title,
    icon,
    children,
    className,
}: PropsWithChildren<{ className?: string; title: ReactNode; icon?: string }>) {
    return (
        <section className={classes("section", className)}>
            <h3 className="section-heading">
                {icon ? <i className="section-heading-icon fas fa-exclamation-triangle"></i> : null}{" "}
                <span className="section-heading-text">{title}</span>
            </h3>
            {children}
        </section>
    );
}

function PlanLabel({ plan }: { plan: IPlanInfo }) {
    return (
        <span className="plan-label">
            <PlanIcon className="plan-label--icon" type={plan.Type} />
            <span className="plan-label--name">{`${labels.PlanType(plan.Type)} / ${plan.Name}`}</span>
        </span>
    );
}

function PlanIcon({ className, type }: { className?: string; type: PlanType }) {
    const icon: string =
        ((type === PlanType.NutritionPlan) || (type === PlanType.NutritionPlan2024))
            ? "icon icon-nutrition"
            : type === PlanType.SeedPlan
            ? "icon icon-reco-seeds"
            : type === PlanType.SprayPlan
            ? "icon icon-sprayer"
            : "far fa-clipboard";
    return icon ? <i className={classes(className, icon)}></i> : null;
}

export const editMultipleCroppingsConfirmationDialogConfig = {
    component: EditMultipleCroppingsConfirmationDialog,
    inject: {},
};
